
import { useState, useMemo, useCallback, useEffect } from 'react';
import { User } from "@/types";
import { useBirdieData } from "@/hooks/useBirdieData";
import { useBirdieRecordsQuery, useInvalidateBirdieQueries } from "@/hooks/useBirdieQueries";

export const useHomeBirdies = (currentUser: User | null, friends: User[], includeSampleData = false) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Create a stable representation of friend IDs (including manual players)
  const stableFriendIds = useMemo(() => {
    return friends.map(f => f.id).sort();
  }, [friends]);

  // Create a stable array of user IDs
  const userIds = useMemo(() => {
    const ids: string[] = [];
    if (currentUser?.id) ids.push(currentUser.id);
    return [...ids, ...stableFriendIds];
  }, [currentUser?.id, stableFriendIds]);

  // Create proper user objects array
  const usersForBirdieData = useMemo(() => {
    const users: User[] = [];
    if (currentUser) users.push(currentUser);
    users.push(...friends);
    return users;
  }, [currentUser, friends]);

  // Use React Query to fetch and cache birdie records
  const {
    data: birdieRecords = [],
    isLoading,
    error
  } = useBirdieRecordsQuery(userIds.length > 0 ? userIds : [], includeSampleData);

  // Get the utility functions from useBirdieData
  const {
    getUserYearBirdies,
    getBirdieLeaders,
    filterUserGames
  } = useBirdieData(usersForBirdieData, birdieRecords, selectedYear);

  // Get functions to invalidate queries when needed
  const { invalidateAllBirdies } = useInvalidateBirdieQueries();

  const refetchBirdieRecords = useCallback(() => {
    invalidateAllBirdies();
  }, [invalidateAllBirdies]);

  useEffect(() => {
    if (error) {
      console.error('Error fetching birdie records:', error);
    }
  }, [error]);

  return {
    birdieRecords,
    isLoadingBirdies: isLoading,
    selectedYear,
    setSelectedYear,
    getUserYearBirdies,
    getBirdieLeaders,
    refetchBirdieRecords,
    filterUserGames
  };
};
